import { useState, useEffect, useCallback } from 'react'
import {
  Table, Button, Space, Input, InputNumber, Modal, Form, Typography, message,
  Popconfirm, Tag, Tooltip, Select, Switch, Drawer, Empty, Divider, Tabs, Checkbox,
} from 'antd'
import { ApiOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import '../../../styles/aurora-page.css'
import '../../../styles/aurora-modal.css'
import {
  listMockRules, getMockServerInfo, getRecentMockLogs, createMockRule,
  updateMockRule, toggleMockRule, deleteMockRule,
} from '@/api/mocks'
import { useProjectStore } from '@/stores/project'
import { useThemeStore } from '@/stores/theme'
import type { MockRule, MockServerInfo, MockHitLog, MockHttpMethod, MockBodyType, ApiKVItem } from '@/types'

const { Title, Text } = Typography

const METHOD_OPTIONS: MockHttpMethod[] = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS', 'ANY']
const BODY_TYPE_OPTIONS: { value: MockBodyType; label: string }[] = [
  { value: 'json', label: 'JSON' },
  { value: 'text', label: 'Text' },
  { value: 'html', label: 'HTML' },
  { value: 'xml', label: 'XML' },
]

const methodColor: Record<string, string> = {
  GET: 'blue',
  POST: 'success',
  PUT: 'orange',
  DELETE: 'red',
  PATCH: 'purple',
  HEAD: 'cyan',
  OPTIONS: 'default',
  ANY: 'geekblue',
}

const monoFont = "'JetBrains Mono', 'Fira Code', 'Consolas', monospace"

interface MockFormValues {
  name: string
  method: MockHttpMethod
  path: string
  status_code: number
  delay_ms: number
  body_type: MockBodyType
  response_body: string
  description: string
  enabled: boolean
  matchQueryEntries: ApiKVItem[]
  matchBodyEntries: ApiKVItem[]
  headerEntries: ApiKVItem[]
}

interface DebugResult {
  ok: boolean
  status: number
  status_text: string
  duration_ms: number
  headers: [string, string][]
  body: string
}

const kvArrayToJson = (entries: ApiKVItem[] | undefined): string => {
  const filtered = (entries || []).filter((e) => e.key.trim() !== '')
  if (filtered.length === 0) return '[]'
  return JSON.stringify(filtered)
}

const jsonToKvArray = (jsonStr: string): ApiKVItem[] => {
  try {
    const parsed = JSON.parse(jsonStr || '[]')
    if (Array.isArray(parsed)) {
      return parsed.map((item) => ({ key: String(item?.key ?? ''), value: String(item?.value ?? ''), remark: '' }))
    }
    return []
  } catch {
    return []
  }
}

/** 状态码标签颜色 */
const statusCodeColor = (code: number): string => {
  if (code < 300) return 'success'
  if (code < 400) return 'processing'
  if (code < 500) return 'warning'
  return 'error'
}

export default function Mock() {
  const [data, setData] = useState<MockRule[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 })
  // 筛选条件
  const [keyword, setKeyword] = useState('')
  const [keywordInput, setKeywordInput] = useState('')
  const [methodFilter, setMethodFilter] = useState<string>('')
  const [enabledFilter, setEnabledFilter] = useState<string>('')
  // 编辑抽屉
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<MockRule | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState('response')
  const [form] = Form.useForm<MockFormValues>()
  const bodyType = Form.useWatch('body_type', form)
  const method = Form.useWatch('method', form)
  // 调试弹框
  const [debugOpen, setDebugOpen] = useState(false)
  const [debugRule, setDebugRule] = useState<MockRule | null>(null)
  const [debugQuery, setDebugQuery] = useState('')
  const [debugBody, setDebugBody] = useState('')
  const [debugSending, setDebugSending] = useState(false)
  const [debugResult, setDebugResult] = useState<DebugResult | null>(null)
  // 日志抽屉
  const [logsOpen, setLogsOpen] = useState(false)
  const [logs, setLogs] = useState<MockHitLog[]>([])
  const [logsLoading, setLogsLoading] = useState(false)
  // 服务信息
  const [serverInfo, setServerInfo] = useState<MockServerInfo | null>(null)

  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0
  const { isDark } = useThemeStore()

  const borderColor = isDark ? 'var(--border)' : 'var(--au-border)'

  const fetchData = useCallback(async (
    page = 1,
    pageSize = 10,
    kw = '',
    method = '',
    enabled = '',
  ) => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await listMockRules({
        project_id: projectId, page, page_size: pageSize,
        keyword: kw, method, enabled,
      })
      setData(res.data)
      setPagination((prev) => ({ ...prev, page, pageSize, total: res.total }))
    } finally {
      setLoading(false)
    }
  }, [projectId])

  const loadServerInfo = useCallback(async () => {
    try {
      setServerInfo(await getMockServerInfo())
    } catch {
      setServerInfo(null)
    }
  }, [])

  useEffect(() => {
    fetchData()
    loadServerInfo()
  }, [fetchData, loadServerInfo])

  // ============ 事件处理 ============

  const handleSearch = () => {
    setKeyword(keywordInput)
    fetchData(1, pagination.pageSize, keywordInput, methodFilter, enabledFilter)
  }

  const handleFilterChange = (method: string, enabled: string) => {
    setMethodFilter(method)
    setEnabledFilter(enabled)
    fetchData(1, pagination.pageSize, keyword, method, enabled)
  }

  const handleCreate = () => {
    setEditingRule(null)
    form.resetFields()
    form.setFieldsValue({
      name: '', method: 'GET', path: '/', status_code: 200, delay_ms: 0,
      body_type: 'json', response_body: '{\n  \n}', description: '', enabled: true,
      matchQueryEntries: [], matchBodyEntries: [], headerEntries: [],
    })
    setActiveTab('response')
    setDrawerOpen(true)
  }

  const handleEdit = (record: MockRule) => {
    setEditingRule(record)
    form.setFieldsValue({
      name: record.name,
      method: record.method,
      path: record.path,
      status_code: record.status_code,
      delay_ms: record.delay_ms,
      body_type: record.body_type,
      response_body: record.response_body,
      description: record.description,
      enabled: record.enabled,
      matchQueryEntries: jsonToKvArray(record.match_query),
      matchBodyEntries: jsonToKvArray(record.match_body),
      headerEntries: jsonToKvArray(record.response_headers),
    })
    setActiveTab('response')
    setDrawerOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteMockRule(id, projectId)
      message.success('删除成功')
      fetchData(pagination.page, pagination.pageSize, keyword, methodFilter, enabledFilter)
    } catch {
      // 错误已在拦截器处理
    }
  }

  const handleToggle = async (record: MockRule) => {
    try {
      const res = await toggleMockRule(record.id, projectId)
      message.success(res.enabled ? '已启用' : '已停用')
      fetchData(pagination.page, pagination.pageSize, keyword, methodFilter, enabledFilter)
    } catch {
      // 错误已在拦截器处理
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)
      const payload = {
        name: values.name,
        method: values.method,
        path: values.path.trim(),
        match_query: kvArrayToJson(values.matchQueryEntries),
        match_body: kvArrayToJson(values.matchBodyEntries),
        status_code: values.status_code,
        delay_ms: values.delay_ms,
        response_headers: kvArrayToJson(values.headerEntries),
        body_type: values.body_type,
        response_body: values.response_body ?? '',
        description: values.description || '',
        enabled: values.enabled,
      }
      if (editingRule) {
        await updateMockRule(editingRule.id, payload, projectId)
        message.success('更新成功')
      } else {
        await createMockRule({ ...payload, project_id: projectId })
        message.success('创建成功')
      }
      setDrawerOpen(false)
      fetchData(pagination.page, pagination.pageSize, keyword, methodFilter, enabledFilter)
    } catch {
      // 表单校验失败或接口错误
    } finally {
      setSubmitting(false)
    }
  }

  /** JSON 响应体格式化 */
  const handleFormatBody = () => {
    const raw = form.getFieldValue('response_body') as string
    if (!raw || !raw.trim()) return
    try {
      form.setFieldsValue({ response_body: JSON.stringify(JSON.parse(raw), null, 2) })
    } catch {
      message.warning('响应体不是合法 JSON，无法格式化')
    }
  }

  // ============ 调试 ============

  const openDebug = (record: MockRule) => {
    setDebugRule(record)
    setDebugQuery('')
    setDebugBody('')
    setDebugResult(null)
    setDebugOpen(true)
  }

  /** 拼接相对调试 URL（走 vite 代理，避免跨域） */
  const buildDebugUrl = (rule: MockRule, query: string): string => {
    const q = query.trim().replace(/^\?/, '')
    return `/mock/${projectId}${rule.path}${q ? `?${q}` : ''}`
  }

  /** 拼接外部系统可直接调用的完整 Mock URL */
  const buildAbsoluteUrl = (rule: MockRule): string => {
    const base = serverInfo?.base_url || `http://${window.location.hostname}:8001/mock`
    return `${base}/${projectId}${rule.path}`
  }

  const handleCopyText = async (text: string, tip = '已复制') => {
    try {
      await navigator.clipboard.writeText(text)
      message.success(tip)
    } catch {
      message.error('复制失败，请手动复制')
    }
  }

  const handleCopyUrl = async (rule: MockRule) => {
    await handleCopyText(buildAbsoluteUrl(rule), 'Mock URL 已复制')
  }

  const handleSendDebug = async () => {
    if (!debugRule) return
    const url = buildDebugUrl(debugRule, debugQuery)
    const hasBody = !['GET', 'HEAD', 'OPTIONS'].includes(debugRule.method)
    setDebugSending(true)
    const start = performance.now()
    try {
      const resp = await fetch(url, {
        method: debugRule.method === 'ANY' ? 'GET' : debugRule.method,
        headers: hasBody && debugBody.trim() ? { 'Content-Type': 'application/json' } : undefined,
        body: hasBody && debugBody.trim() ? debugBody : undefined,
        cache: 'no-store',
      })
      const text = await resp.text()
      const headers: [string, string][] = []
      resp.headers.forEach((value, key) => headers.push([key, value]))
      setDebugResult({
        ok: resp.ok,
        status: resp.status,
        status_text: resp.statusText,
        duration_ms: Math.round(performance.now() - start),
        headers,
        body: text,
      })
      // 调试命中后刷新命中统计
      fetchData(pagination.page, pagination.pageSize, keyword, methodFilter, enabledFilter)
    } catch (e) {
      setDebugResult({
        ok: false, status: 0, status_text: '网络错误',
        duration_ms: Math.round(performance.now() - start),
        headers: [], body: String(e),
      })
    } finally {
      setDebugSending(false)
    }
  }

  // ============ 命中日志 ============

  const openLogs = async () => {
    setLogsOpen(true)
    setLogsLoading(true)
    try {
      setLogs(await getRecentMockLogs(200))
    } finally {
      setLogsLoading(false)
    }
  }

  // ============ KV 编辑器（表格样式，与接口编辑抽屉一致） ============

  const renderKvFormList = (name: keyof MockFormValues, keyPlaceholder: string, valuePlaceholder: string) => (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 1.2fr 60px',
            gap: 0,
            padding: '8px 12px',
            background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
            borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
            color: isDark ? 'var(--text-light)' : 'var(--au-slate)',
            fontWeight: 500,
            fontSize: 13,
            alignItems: 'center',
          }}>
            <Checkbox disabled defaultChecked />
            <span>{keyPlaceholder}</span>
            <span>{valuePlaceholder}</span>
            <Button type="link" size="small" onClick={() => add({ key: '', value: '', remark: '' })} style={{ padding: 0 }}>
              添加
            </Button>
          </div>

          {fields.length === 0 && (
            <div style={{ padding: '24px 0', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
              暂无数据，点击右上角"添加"新增
            </div>
          )}

          {fields.map(({ key, name: fieldName, ...restField }, index) => (
            <div
              key={key}
              style={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 1.2fr 60px',
                gap: 0,
                padding: '4px 12px',
                alignItems: 'center',
                borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
                background: isDark
                  ? (index % 2 === 0 ? 'var(--bg)' : 'var(--card)')
                  : (index % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'),
              }}
            >
              <Checkbox defaultChecked />
              <Form.Item {...restField} name={[fieldName, 'key']} style={{ marginBottom: 0, marginRight: 8 }}
                rules={[{ required: true, message: '请输入' }]}>
                <Input size="small" placeholder={keyPlaceholder} bordered={false} />
              </Form.Item>
              <Form.Item {...restField} name={[fieldName, 'value']} style={{ marginBottom: 0, marginRight: 8 }}>
                <Input size="small" placeholder={valuePlaceholder} bordered={false} style={{ background: isDark ? 'var(--bg)' : undefined, color: isDark ? 'var(--text)' : undefined }} />
              </Form.Item>
              <Button type="text" size="small" danger onClick={() => remove(fieldName)} style={{ padding: 0 }}>
                删除
              </Button>
            </div>
          ))}
        </div>
      )}
    </Form.List>
  )

  // ============ 列定义 ============

  const columns: ColumnsType<MockRule> = [
    {
      title: '规则名称',
      dataIndex: 'name',
      width: 180,
      ellipsis: true,
      render: (name: string, record) => (
        <Space size={6}>
          <ApiOutlined style={{ color: record.enabled ? 'var(--au-primary)' : 'var(--au-text-faint)' }} />
          <span style={{ fontWeight: 500, color: record.enabled ? 'var(--au-slate)' : 'var(--au-text-faint)' }}>{name}</span>
        </Space>
      ),
    },
    {
      title: '方法',
      dataIndex: 'method',
      width: 90,
      render: (v: string) => (
        <Tag color={methodColor[v] || 'default'} style={{ margin: 0, minWidth: 56, textAlign: 'center' }}>{v}</Tag>
      ),
    },
    {
      title: 'Mock 路径',
      dataIndex: 'path',
      width: 260, /* 限宽避免长路径撑宽表格出现横向滚动条（超长由 ellipsis 截断 + Tooltip 兜底） */
      ellipsis: true,
      render: (v: string, record) => (
        <Tooltip title={buildAbsoluteUrl(record)}>
          <Text code copyable={false} style={{ fontSize: 12, fontFamily: monoFont, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {v}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '状态码',
      dataIndex: 'status_code',
      width: 90,
      render: (v: number) => <Tag color={statusCodeColor(v)} style={{ margin: 0 }}>{v}</Tag>,
    },
    {
      title: '延迟',
      dataIndex: 'delay_ms',
      width: 80,
      render: (v: number) => (
        <span style={{ fontSize: 13, color: v > 0 ? 'var(--au-slate)' : 'var(--au-text-faint)' }}>{v}ms</span>
      ),
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      width: 70,
      render: (v: boolean, record) => (
        <Switch size="small" checked={v} onChange={() => handleToggle(record)} />
      ),
    },
    {
      title: '命中统计',
      dataIndex: 'hit_count',
      width: 130,
      render: (v: number, record) => (
        <div>
          <div style={{ fontSize: 13, color: 'var(--au-slate)' }}>{v} 次</div>
          <div style={{ fontSize: 11, color: 'var(--au-text-faint)' }}>
            {record.last_hit_time ? new Date(record.last_hit_time).toLocaleString('zh-CN') : '从未命中'}
          </div>
        </div>
      ),
    },
    {
      title: '操作',
      width: 190,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => openDebug(record)}>调试</Button>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" size="small" onClick={() => handleCopyUrl(record)}>复制</Button>
          <Popconfirm
            title="确定删除此 Mock 规则？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const sectionStyle = {
    background: 'var(--au-header-bg)',
    borderRadius: 10,
    border: `1px solid ${borderColor}`,
    padding: '10px 16px',
    marginBottom: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap' as const,
  }

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>Mock服务</Title>
        <Space>
          <Button onClick={openLogs}>命中日志</Button>
          <Button
            onClick={() => {
              fetchData(pagination.page, pagination.pageSize, keyword, methodFilter, enabledFilter)
              loadServerInfo()
            }}
          >
            刷新
          </Button>
          <Button
            type="primary"
            disabled={!projectId}
            onClick={handleCreate}
            style={{ background: 'var(--au-primary)', border: 'none' }}
          >
            新建Mock规则
          </Button>
        </Space>
      </div>

      {/* 服务状态条 */}
      <div style={sectionStyle}>
        <Space size={12} wrap>
          <Tag color={serverInfo?.running ? 'success' : 'error'} style={{ margin: 0 }}>
            {serverInfo?.running ? '运行中' : '已停止'}
          </Tag>
          <Text style={{ fontSize: 13, color: 'var(--au-slate)' }}>
            Mock 服务地址：
          </Text>
          <Text code
            style={{ fontSize: 12, fontFamily: monoFont }}>
            {serverInfo ? `${serverInfo.base_url}/${projectId}/你的接口路径` : '加载中...'}
          </Text>
          {serverInfo && (
            <Button
              type="text"
              size="small"
              onClick={() => handleCopyText(`${serverInfo.base_url}/${projectId}/`)}
            >
              复制
            </Button>
          )}
        </Space>
        <Text style={{ fontSize: 12, color: 'var(--au-text-faint)' }}>
          参数不做 QUERY/REST 区分，可写在路径中（? 或 / 后均可），如 /api/users/{'{userId}'}?verbose=1
        </Text>
      </div>

      <div className="search-bar">
        <Space>
          <Select
            placeholder="请求方法"
            allowClear
            style={{ width: 120 }}
            value={methodFilter || undefined}
            onChange={(v) => handleFilterChange(v || '', enabledFilter)}
            options={METHOD_OPTIONS.map((m) => ({ value: m, label: m }))}
          />
          <Select
            placeholder="启用状态"
            allowClear
            style={{ width: 120 }}
            value={enabledFilter || undefined}
            onChange={(v) => handleFilterChange(methodFilter, v || '')}
            options={[
              { value: 'enabled', label: '已启用' },
              { value: 'disabled', label: '已停用' },
            ]}
          />
          <Space.Compact>
            <Input
              placeholder="搜索规则名称或路径"
              allowClear
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onPressEnter={handleSearch}
              style={{ minWidth: 200 }}
            />
            <Button type="primary" onClick={handleSearch} style={{ background: 'var(--au-primary)', border: 'none' }}>
              查询
            </Button>
          </Space.Compact>
        </Space>
      </div>

      <div className="content-card">
        {!projectId ? (
          <Empty description="请先在顶部选择项目" style={{ padding: '48px 0' }} />
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            scroll={{ y: '100%' }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条`,
              onChange: (p, ps) => fetchData(p, ps, keyword, methodFilter, enabledFilter),
            }}
          />
        )}
      </div>

      {/* 新增/编辑抽屉（对齐接口编辑抽屉样式） */}
      <Drawer
        title={null}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="right"
        width="70%"
        rootClassName="aurora-edit-drawer"
        rootStyle={{ top: 56, height: 'calc(100vh - 56px)' }}
        maskStyle={{ top: 56, height: 'calc(100vh - 56px)', background: 'transparent' }}
        bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column' }}
        headerStyle={{ display: 'none' }}
      >
        <Form form={form} layout="vertical" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Form.Item name="body_type" hidden>
            <Input />
          </Form.Item>
          {/* 顶部：方法标签 + 规则名称 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 24px',
            borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
            background: isDark ? 'var(--card)' : '#ffffff',
            flexShrink: 0,
            gap: 12,
          }}>
            <Tag
              color={methodColor[method] || 'default'}
              style={{ fontSize: 14, fontWeight: 600, padding: '2px 10px', margin: 0 }}
            >
              {method}
            </Tag>
            <Form.Item name="name" style={{ margin: 0, flex: 1 }}
              rules={[{ required: true, message: '请输入规则名称' }]}>
              <Input
                bordered={false}
                placeholder="如：用户列表接口 Mock"
                maxLength={128}
                style={{ fontSize: 16, fontWeight: 600 }}
              />
            </Form.Item>
          </div>

          {/* Mock 行：方法 + 路径 + 状态码 + 延迟 + 保存 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 24px',
            borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
            background: isDark ? 'var(--card)' : '#ffffff',
            flexShrink: 0,
          }}>
            <div style={{
              width: 110,
              border: '1px solid var(--au-border)',
              borderRadius: '4px 0 0 4px',
            }}>
              <Form.Item name="method" style={{ margin: 0 }} rules={[{ required: true, message: '请选择请求方法' }]}>
                <Select
                  options={METHOD_OPTIONS.map((m) => ({ value: m, label: m === 'ANY' ? 'ANY' : m }))}
                  optionRender={(opt) => (
                    <span style={{ color: methodColor[opt.value as string], fontWeight: 600 }}>
                      {opt.label}{opt.value === 'ANY' ? '（任意方法）' : ''}
                    </span>
                  )}
                  labelRender={(props) => (
                    <span style={{ color: methodColor[props.value as string] || undefined, fontWeight: 600 }}>
                      {props.label}
                    </span>
                  )}
                  bordered={false}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
            <Form.Item name="path" style={{ margin: 0, flex: 1 }}
              rules={[
                { required: true, message: '请输入请求路径' },
                { pattern: /^\/\S*$/, message: '路径必须以 / 开头且不含空白' },
              ]}>
              <Input
                placeholder="/api/users/{userId}"
                style={{ borderRadius: 0, borderLeft: 'none', fontFamily: monoFont }}
              />
            </Form.Item>
            <div style={{
              width: 140,
              border: '1px solid var(--au-border)',
              borderLeft: 'none',
              display: 'flex',
              alignItems: 'center',
              padding: '0 8px 0 12px',
              gap: 6,
            }}>
              <span style={{ fontSize: 12, color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', flexShrink: 0 }}>状态码</span>
              <Form.Item name="status_code" style={{ margin: 0, flex: 1 }}
                rules={[{ required: true, message: '请输入状态码' }]}>
                <InputNumber min={100} max={599} bordered={false} controls={false} style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <div style={{
              width: 140,
              border: '1px solid var(--au-border)',
              borderLeft: 'none',
              borderRadius: '0 4px 4px 0',
              display: 'flex',
              alignItems: 'center',
              padding: '0 8px 0 12px',
              gap: 6,
            }}>
              <span style={{ fontSize: 12, color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', flexShrink: 0 }}>延迟ms</span>
              <Form.Item name="delay_ms" style={{ margin: 0, flex: 1 }}>
                <InputNumber min={0} max={60000} bordered={false} controls={false} placeholder="0" style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <Button
              type="primary"
              className="pill-btn"
              loading={submitting}
              onClick={handleSubmit}
              style={{ marginLeft: 10, background: 'var(--au-primary)', border: 'none' }}
            >
              保存
            </Button>
          </div>
          <div style={{
            padding: '4px 24px 8px', fontSize: 12, color: 'var(--au-slate-muted)', lineHeight: '20px',
            flexShrink: 0, background: isDark ? 'var(--card)' : '#ffffff',
          }}>
            <div>
              QUERY 参数与 REST 参数不做区分：可统一写在路径中，支持 {'{param}'} 占位符，参数可跟在 ? 或 / 之后，如 /api/users/{'{userId}'}?verbose=1
            </div>
            {editingRule && (
              <div>完整地址：{buildAbsoluteUrl(editingRule)}</div>
            )}
          </div>

          {/* Tabs 区域 */}
          <div style={{
            flex: '1 1 200px',
            minHeight: 200,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              tabBarStyle={{ paddingLeft: 24 }}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
              items={[
                {
                  key: 'query',
                  label: '匹配参数',
                  children: (
                    <div style={{ padding: '0 24px 16px' }}>
                      <div style={{ fontSize: 12, color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', marginBottom: 8 }}>
                        请求需携带以下全部查询参数才会命中该规则；留空表示不校验查询参数
                      </div>
                      {renderKvFormList('matchQueryEntries', '参数名称', '匹配值')}
                    </div>
                  ),
                },
                {
                  key: 'matchBody',
                  label: '匹配请求体',
                  children: (
                    <div style={{ padding: '0 24px 16px' }}>
                      <div style={{ fontSize: 12, color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', marginBottom: 8, lineHeight: '20px' }}>
                        用于校验请求的 JSON Body：请求 Body 中对应字段的值与期望值全部相等时才命中该规则。
                        字段路径从 Body 根节点逐层往下写，用点号分隔（对象取属性、数组取下标），留空表示不校验请求体。
                        示例：配置 user.name = alice，则请求体 {'{"user": {"name": "alice"}}'} 才会命中。
                      </div>
                      {renderKvFormList('matchBodyEntries', '字段路径（如 data.user.name）', '期望值')}
                    </div>
                  ),
                },
                {
                  key: 'headers',
                  label: '响应头',
                  children: (
                    <div style={{ padding: '0 24px 16px' }}>
                      {renderKvFormList('headerEntries', 'Header 名', 'Header 值')}
                    </div>
                  ),
                },
                {
                  key: 'response',
                  label: '响应定义',
                  children: (
                    <div style={{ padding: '0 24px 16px' }}>
                      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          {BODY_TYPE_OPTIONS.map((opt) => (
                            <Text
                              key={opt.value}
                              onClick={() => form.setFieldsValue({ body_type: opt.value })}
                              style={{
                                cursor: 'pointer',
                                padding: '4px 12px',
                                marginRight: 8,
                                borderRadius: 4,
                                fontSize: 13,
                                color: bodyType === opt.value ? 'var(--au-primary)' : (isDark ? '#94A3B8' : 'var(--au-slate-muted)'),
                                background: bodyType === opt.value ? (isDark ? 'rgba(255,255,255,0.12)' : 'var(--au-primary-50)') : (isDark ? 'rgba(255,255,255,0.06)' : 'var(--au-row-alt)'),
                                border: bodyType === opt.value ? '1px solid var(--au-primary)' : (isDark ? '1px solid var(--border)' : '1px solid transparent'),
                              }}
                            >
                              {opt.label}
                            </Text>
                          ))}
                        </div>
                        {bodyType === 'json' && (
                          <Button size="small" onClick={handleFormatBody}>格式化</Button>
                        )}
                      </div>
                      <Form.Item
                        name="response_body"
                        style={{ marginBottom: 0 }}
                        rules={[{
                          validator: (_, value: string) => {
                            if (!value || !value.trim() || bodyType !== 'json') return Promise.resolve()
                            try {
                              JSON.parse(value)
                              return Promise.resolve()
                            } catch {
                              return Promise.reject(new Error('响应体不是合法的 JSON'))
                            }
                          },
                        }]}
                      >
                        <Input.TextArea
                          rows={14}
                          placeholder={bodyType === 'json' ? '{\n  "code": 0,\n  "data": []\n}' : '响应内容'}
                          style={{ fontFamily: monoFont, fontSize: 12 }}
                        />
                      </Form.Item>
                    </div>
                  ),
                },
                {
                  key: 'settings',
                  label: '设置',
                  children: (
                    <div style={{ padding: '0 24px 16px', maxWidth: 640 }}>
                      <Form.Item label="启用状态" name="enabled" valuePropName="checked" style={{ marginBottom: 16 }}>
                        <Switch checkedChildren="启用" unCheckedChildren="停用" />
                      </Form.Item>
                      <Form.Item label="描述" name="description" style={{ marginBottom: 0 }}>
                        <Input.TextArea rows={3} placeholder="规则用途说明（选填）" maxLength={500} />
                      </Form.Item>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </Form>
      </Drawer>

      {/* 调试弹框 */}
      <Modal
        title={debugRule ? <span>调试 Mock：<Text code style={{ fontFamily: monoFont }}>{debugRule.method} {debugRule.path}</Text></span> : '调试 Mock'}
        open={debugOpen}
        onCancel={() => setDebugOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
        wrapClassName="aurora-modal-wrap"
        className="aurora-modal"
      >
        {debugRule && (
          <div style={{ marginTop: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <Tag color={methodColor[debugRule.method] || 'default'} style={{ margin: 0 }}>{debugRule.method}</Tag>
              <Text code style={{ fontSize: 12, fontFamily: monoFont }}>
                {buildDebugUrl(debugRule, debugQuery)}
              </Text>
              <Button size="small" type="text" onClick={() => handleCopyUrl(debugRule)}>复制地址</Button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 13, color: 'var(--au-slate)' }}>查询参数（选填）</Text>
              <Input
                placeholder="a=1&b=2"
                value={debugQuery}
                onChange={(e) => setDebugQuery(e.target.value)}
                style={{ marginTop: 4, fontFamily: monoFont }}
              />
            </div>

            {!['GET', 'HEAD', 'OPTIONS'].includes(debugRule.method) && (
              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: 13, color: 'var(--au-slate)' }}>请求体（选填）</Text>
                <Input.TextArea
                  rows={3}
                  placeholder='{"key": "value"}'
                  value={debugBody}
                  onChange={(e) => setDebugBody(e.target.value)}
                  style={{ marginTop: 4, fontFamily: monoFont, fontSize: 12 }}
                />
              </div>
            )}

            <Button
              type="primary"
              loading={debugSending}
              onClick={handleSendDebug}
              style={{ background: 'var(--au-primary)', border: 'none', marginBottom: 12 }}
            >
              发送请求
            </Button>

            {debugResult && (
              <div>
                <Divider style={{ margin: '8px 0 12px' }} />
                <Space size={12} style={{ marginBottom: 8 }}>
                  <Tag color={debugResult.ok ? 'success' : 'error'} style={{ margin: 0 }}>
                    {debugResult.status > 0 ? `${debugResult.status} ${debugResult.status_text}` : debugResult.status_text}
                  </Tag>
                  <Text style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>耗时 {debugResult.duration_ms}ms</Text>
                </Space>
                {debugResult.headers.length > 0 && (
                  <div style={{
                    border: `1px solid ${borderColor}`, borderRadius: 8, overflow: 'hidden',
                    fontSize: 12, marginBottom: 10,
                  }}>
                    <div style={{
                      display: 'grid', gridTemplateColumns: '200px 1fr', padding: '6px 12px',
                      background: 'var(--au-header-bg)', color: 'var(--au-slate)', fontWeight: 600,
                      borderBottom: `1px solid ${borderColor}`,
                    }}>
                      <span>响应头</span><span>值</span>
                    </div>
                    {debugResult.headers.map(([k, v], idx) => (
                      <div key={`${k}-${idx}`} style={{
                        display: 'grid', gridTemplateColumns: '200px 1fr', padding: '4px 12px',
                        borderBottom: idx < debugResult.headers.length - 1 ? `1px solid ${borderColor}` : 'none',
                        color: 'var(--au-slate)', fontFamily: monoFont,
                      }}>
                        <span>{k}</span>
                        <span style={{ wordBreak: 'break-all' }}>{v}</span>
                      </div>
                    ))}
                  </div>
                )}
                <Text strong style={{ fontSize: 13, color: 'var(--au-slate)' }}>响应内容</Text>
                <pre style={{
                  margin: '4px 0 0', fontSize: 12, fontFamily: monoFont, whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all', lineHeight: 1.7, padding: '12px 16px', borderRadius: 8,
                  border: `1px solid ${borderColor}`, maxHeight: 320, overflow: 'auto',
                  background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
                  color: isDark ? 'var(--text)' : 'var(--au-slate)',
                }}>
                  {debugResult.body || '（空响应体）'}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 命中日志抽屉 */}
      <Drawer
        title="Mock 命中日志"
        open={logsOpen}
        onClose={() => setLogsOpen(false)}
        width={560}
        rootClassName="aurora-edit-drawer"
        extra={
          <Button
            size="small"
            onClick={async () => {
              setLogsLoading(true)
              try { setLogs(await getRecentMockLogs(200)) } finally { setLogsLoading(false) }
            }}
          >
            刷新
          </Button>
        }
      >
        {logs.length === 0 && !logsLoading ? (
          <Empty description="暂无命中记录，Mock 服务被调用后将在此展示" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div>
            {logs.map((log, idx) => (
              <div key={idx} style={{
                padding: '10px 12px', borderRadius: 10, marginBottom: 8,
                border: `1px solid ${borderColor}`,
                background: log.hit ? 'var(--au-surface)' : 'var(--au-terracotta-light)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                  <Tag color={methodColor[log.method] || 'default'} style={{ margin: 0 }}>{log.method}</Tag>
                  <Text code style={{ fontSize: 12, fontFamily: monoFont }}>
                    {log.path}{log.query ? `?${log.query}` : ''}
                  </Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--au-slate-muted)', flexWrap: 'wrap' }}>
                  <span>{log.time}</span>
                  <Tag color={log.hit ? statusCodeColor(log.status) : 'error'} style={{ margin: 0 }}>{log.status}</Tag>
                  <span>{log.duration_ms}ms</span>
                  {log.client && <span>来源：{log.client}</span>}
                  <span style={{ color: log.hit ? 'var(--au-slate-muted)' : 'var(--au-terracotta)', fontWeight: 500 }}>
                    {log.hit ? `命中规则：${log.rule_name}` : '未命中任何规则'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Drawer>
    </div>
  )
}
